import React, { memo, useCallback, useState, useEffect } from "react";
import {
  GoogleMap,
  InfoWindowF,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { PurpleTick, BlueTick, Greentick } from "../../assets/images/index";
import "./style.scss";

function Map(props) {
  const { data: mapsDatas, sites, onFilter, onClear, activeMap } = props;

  const center = { lat: 53.689659, lng: -2.522821 };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
  });

  const [map, setMap] = useState(null);
  const [popupInfo, onMarker] = useState(null);

  const mapIcons =
    isLoaded && window.google && window.google.maps
      ? {
          donor: {
            url: BlueTick,
            scaledSize: new window.google.maps.Size(32, 32),
          },
          receiver: {
            url: Greentick,
            scaledSize: new window.google.maps.Size(36, 36),
          },
          soil_treatment: {
            url: PurpleTick,
            scaledSize: new window.google.maps.Size(36, 36),
          },
        }
      : null;

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const getIconForType = (type) => {
    if (type === "receiver") {
      return Greentick;
    }

    if (type === "soil_treatment") {
      return PurpleTick;
    }

    return BlueTick;
  };

  const getIconObjectForType = (type) => {
    if (!mapIcons) {
      return null;
    }

    if (type === "receiver") {
      return mapIcons.receiver;
    }

    if (type === "soil_treatment") {
      return mapIcons.soil_treatment;
    }

    return mapIcons.donor;
  };

  const getTypeLabel = (type) => {
    if (type === "donor") {
      return "Donor";
    }

    if (type === "receiver") {
      return "Receiver";
    }

    if (type === "soil_treatment") {
      return "Soil Treatment";
    }

    return type || "Site";
  };

  const groupedMapData = Object.values(
    (mapsDatas || []).reduce((acc, el) => {
      if (!el || !el.position) {
        return acc;
      }

      const lat = parseFloat(el.position[0]);
      const lng = parseFloat(el.position[1]);

      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        return acc;
      }

      const key = `${lat.toFixed(5)}-${lng.toFixed(5)}`;

      if (!acc[key]) {
        acc[key] = {
          key,
          lat,
          lng,
          items: [],
        };
      }

      acc[key].items.push(el);

      return acc;
    }, {})
  );

  const getGroupType = (items) => {
    if (!items || items.length === 0) {
      return "donor";
    }

    const types = [...new Set(items.map((item) => item.type))];

    if (types.length === 1) {
      return types[0];
    }

    return items[0]?.type || "donor";
  };

  const getGroupedMarkerIcon = (type, count) => {
    const colors = {
      donor: "#1e88e5",
      receiver: "#2e7d32",
      soil_treatment: "#7b61ff",
    };

    const color = colors[type] || colors.donor;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M28 2C16.4 2 7 11.4 7 23c0 15.5 21 29.6 21 29.6s21-14.1 21-29.6C49 11.4 39.6 2 28 2z" fill="${color}"/>
        <circle cx="28" cy="23" r="12" fill="white"/>
        <text x="28" y="28" text-anchor="middle" font-size="12" font-weight="700" fill="${color}">${count}</text>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(56, 56),
      anchor: new window.google.maps.Point(28, 56),
    };
  };

  return isLoaded ? (
    <div>
      <div className="clearWrapper">
        <div className="wrapBlk">
          {sites.map((item, key) => {
            return (
              <div key={key}>
                <div className="textBlk">
                  <img
                    src={getIconForType(item.key)}
                    alt=""
                    style={{
                      width: item.key === "donor" ? "20px" : "24px",
                      height: "36px",
                      objectFit: "contain",
                    }}
                  />

                  <div
                    onClick={() => onFilter(item)}
                    className={activeMap?.value === item.value ? "activeLabel" : ""}
                  >
                    {item.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div onClick={() => onClear()} className="clearLink">
          Clear Selection
        </div>
      </div>

      <div className="map_sec_content_wrapper">
        <GoogleMap
          mapContainerClassName="map_container"
          center={center}
          zoom={6}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {mapIcons &&
            groupedMapData.map((group) => {
              const count = group.items.length;
              const groupType = getGroupType(group.items);
              const iconObject = getIconObjectForType(groupType);

              if (!iconObject || Number.isNaN(group.lat) || Number.isNaN(group.lng)) {
                return null;
              }

              return (
                <MarkerF
                  id={group.key}
                  key={group.key}
                  onClick={() => {
                    onMarker(group.key);
                  }}
                  icon={count > 1 ? getGroupedMarkerIcon(groupType, count) : iconObject}
                  zIndex={count > 1 ? 1000 : 500}
                  animation="DROP"
                  position={{
                    lat: group.lat,
                    lng: group.lng,
                  }}
                >
                  {popupInfo === group.key && (
                    <InfoWindowF onCloseClick={() => onMarker(null)}>
                      <div className="leafletPopup">
                        {count === 1 && (
                          <>
                            <div className="headRef">{group.items[0].ref}</div>
                            <div className="addressHead">
                              {group.items[0].address}
                            </div>
                          </>
                        )}

                        {count > 1 && (
                          <>
                            <div className="headRef">
                              {count} sites at this location
                            </div>

                            <div className="addressHead">
                              {group.items[0].address}
                            </div>

                            <div style={{ marginTop: "8px" }}>
                              {group.items.map((item) => {
                                return (
                                  <div
                                    key={item.ref}
                                    style={{
                                      marginBottom: "6px",
                                      paddingBottom: "6px",
                                      borderBottom: "1px solid #ddd",
                                    }}
                                  >
                                    <strong>{item.ref}</strong>
                                    {" - "}
                                    {getTypeLabel(item.type)}
                                    <br />
                                    {item.address}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </InfoWindowF>
                  )}
                </MarkerF>
              );
            })}
        </GoogleMap>

        <div className="map_content">
          <p className="home_title_sub_content">
            The full Register is available in the table below and the site locations are pinned on the map. Navigating the map may help you to identify sites of interest. The reference numbers on the pins correspond to the entries on the Register.
          </p>
          <p className="home_title_sub_content">
            Please check the record in the appropriate Donor, Receiver or Soil Treatment Facility list for further site details and to enquire about the availability of materials.
          </p>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
}

export default memo(Map);